/**
 * Issued Tickets — Digital Companion complaint tickets (top-level /dashboard/tickets page).
 *
 * 2026-09-25: rebuilt to the "v1 queue table" design with the Calm neutral
 * colour theme (Docs/mockups/issued-tickets/v1-queue-table-calm.html):
 *   - KPI tiles from GET /complaint-tickets/summary;
 *   - server-side search / status / priority / sort / paging (the old page
 *     loaded the first 100 tickets and filtered those in the browser);
 *   - details in a right-side drawer so the list stays visible;
 *   - Resolve / Close / Reopen / Assign-to-me driven by each ticket's
 *     `permissions` block, so staff only see actions the server accepts;
 *   - colour is reserved for what needs attention (SLA breached, critical,
 *     safety) — everything else is neutral.
 * Action errors show as toasts; they no longer replace the table.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Loader2,
  MessageSquare,
  RefreshCw,
  Search,
  Sparkles,
  Ticket,
} from 'lucide-react';
import { complaintTicketsAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { toast } from '../ui/use-toast';
import { cn } from '../../lib/utils';

type TicketPermissions = {
  can_update_status: boolean;
  can_close: boolean;
  can_reopen: boolean;
  can_change_priority: boolean;
  can_comment: boolean;
  can_assign_self: boolean;
  can_assign_others: boolean;
  can_merge: boolean;
};

export type ComplaintTicketRow = {
  id: number;
  ticket_number: string;
  category_id: string;
  // 2026-10-01: a list -- one label, or one per issue when the contact
  // raised several issues in one message (string from older servers).
  category_label?: string[] | string | null;
  status: string;
  priority?: string | null;
  sla_due_at?: string | null;
  assigned_agent_id?: number | null;
  collected_answers?: Record<string, string>;
  // Detail endpoint only: collected_answers labelled with the question text.
  answers?: Array<{ question_id: string; question: string; answer: string }>;
  events?: Array<{
    id: number;
    event_type: string;
    actor_user_id?: number | null;
    created_at?: string | null;
    payload?: Record<string, any>;
  }>;
  summary?: string | null;
  persona_name?: string | null;
  resolution_mode?: string;
  assigned_agent?: { id: number; name: string; email: string } | null;
  contact?: { id: number; name?: string; phone_number?: string } | null;
  conversation_id?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
  closed_at?: string | null;
  is_safety_escalation?: boolean;
  permissions?: TicketPermissions;
  // 2026-09-24 (Docs/RELEASE_NOTES_2026-09-24_FORMS.md): set when the intake
  // continued after the contact declined / didn't finish a required form.
  registration_incomplete?: { status?: string; missing_fields?: string[]; session_id?: number } | null;
  // 2026-10-04 (Docs/DC_MULTI_ISSUE_ACCURACY_PLAN.md 5.8): how the Digital
  // Companion decided each issue, and whether an agent must check them.
  needs_review?: boolean;
  issue_decisions?: IssueDecision[];
};

type IssueDecision = {
  category_id: string;
  decision: 'auto' | 'clarified' | 'review' | 'agent';
  fit?: string | null;
  confidence?: number | null;
  runner_up?: string | null;
  needs_review?: boolean;
  main?: boolean;
  reason?: string | null;
  question?: string | null;
  answer?: string | null;
  label?: string | null;
};

const DECISION_LABELS: Record<string, string> = {
  auto: 'Detected',
  clarified: 'Clarified with contact',
  review: 'Unsure - check',
  agent: 'Added by an agent',
};

const CORRECTION_REASONS: { value: 'wrong_category' | 'missed_issue' | 'extra_issue' | 'not_a_ticket' | 'split_wrong'; label: string }[] = [
  { value: 'wrong_category', label: 'Wrong category' },
  { value: 'missed_issue', label: 'An issue was missed' },
  { value: 'extra_issue', label: 'An extra issue was added' },
  { value: 'split_wrong', label: 'One problem was split in two' },
  { value: 'not_a_ticket', label: 'Not a ticket' },
];

type Summary = {
  total: number;
  by_status: Record<string, number>;
  open: number;
  open_assigned_to_me: number;
  open_unassigned: number;
  sla_breached: number;
};

const PAGE_SIZE = 25;

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting_on_customer: 'Waiting on customer',
  resolved: 'Resolved',
  closed: 'Closed',
  cancelled: 'Cancelled',
};
const OPEN_STATUSES = ['open', 'in_progress', 'waiting_on_customer'];
const DONE_STATUSES = ['resolved', 'closed', 'cancelled'];

// Tabs map to the list endpoint's comma-separated `status` filter.
const STATUS_TABS: Array<{ key: string; label: string; statuses: string[] | null }> = [
  { key: 'active', label: 'Active', statuses: OPEN_STATUSES },
  { key: 'open', label: 'Open', statuses: ['open'] },
  { key: 'in_progress', label: 'In progress', statuses: ['in_progress'] },
  { key: 'waiting_on_customer', label: 'Waiting', statuses: ['waiting_on_customer'] },
  { key: 'done', label: 'Done', statuses: DONE_STATUSES },
  { key: 'all', label: 'All', statuses: null },
];

const PRIORITIES = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

type SortMode = 'attention' | 'updated' | 'newest' | 'oldest';
const SORT_OPTIONS: Array<{ value: SortMode; label: string }> = [
  { value: 'attention', label: 'Needs attention first' },
  { value: 'updated', label: 'Recently updated' },
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
];

// Calm neutral: one grey family; red only for what needs attention.
const ALERT_TEXT = 'text-red-600 dark:text-red-400';

// ─── helpers ────────────────────────────────────────────────────────────

const errorMessage = (err: any, fallback: string): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return err?.message || fallback;
};

const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
};

// The ticket's category label(s) for display: several issues raised in one
// message are joined with " + "; falls back to the category id.
const categoryText = (t: Pick<ComplaintTicketRow, 'category_label' | 'category_id'>): string => {
  const labels = Array.isArray(t.category_label) ? t.category_label : t.category_label ? [t.category_label] : [];
  return labels.filter(Boolean).join(' + ') || t.category_id;
};

const contactName = (t: ComplaintTicketRow): string => {
  const name = t.contact?.name?.trim();
  if (name) return name;
  const phone = t.contact?.phone_number?.trim();
  if (phone) return `WhatsApp user (…${phone.slice(-4)})`;
  return 'Unknown contact';
};

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const timeAgo = (iso?: string | null): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const duration = (ms: number): string => {
  const minutes = Math.round(Math.abs(ms) / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
};

type SlaInfo = { state: 'breached' | 'at_risk' | 'on_track' | 'done' | 'none'; text: string; fraction: number | null };

// Mirrors app.services.sla_service.sla_state_for (at risk = 80% of the window used).
const slaInfo = (t: ComplaintTicketRow): SlaInfo => {
  if (DONE_STATUSES.includes(t.status)) return { state: 'done', text: STATUS_LABELS[t.status] || t.status, fraction: null };
  if (!t.sla_due_at) return { state: 'none', text: 'No SLA', fraction: null };
  const due = new Date(t.sla_due_at).getTime();
  if (Number.isNaN(due)) return { state: 'none', text: 'No SLA', fraction: null };
  const now = Date.now();
  if (now >= due) return { state: 'breached', text: `Breached ${duration(now - due)} ago`, fraction: 1 };
  const created = t.created_at ? new Date(t.created_at).getTime() : NaN;
  const windowMs = due - created;
  const fraction = !Number.isNaN(created) && windowMs > 0 ? Math.min(1, Math.max(0, (now - created) / windowMs)) : 0;
  return { state: fraction >= 0.8 ? 'at_risk' : 'on_track', text: `${duration(due - now)} left`, fraction };
};

// ─── small pieces ───────────────────────────────────────────────────────

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const done = DONE_STATUSES.includes(status);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold',
        status === 'open' ? 'text-foreground' : done ? 'text-muted-foreground' : 'text-foreground/80'
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', done ? 'border border-current' : 'bg-current')} />
      {STATUS_LABELS[status] || status.replace(/_/g, ' ')}
    </span>
  );
};

const PriorityLabel: React.FC<{ priority?: string | null }> = ({ priority }) => {
  const p = priority || 'normal';
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-semibold',
        p === 'critical' ? ALERT_TEXT : p === 'high' ? 'text-foreground' : 'text-muted-foreground'
      )}
    >
      <span
        className={cn(
          'h-2 w-2 rounded-[2px]',
          p === 'critical' ? 'bg-red-600 dark:bg-red-400' : p === 'high' ? 'bg-foreground/80' : 'bg-muted-foreground/50'
        )}
      />
      {p.charAt(0).toUpperCase() + p.slice(1)}
    </span>
  );
};

const SlaCell: React.FC<{ ticket: ComplaintTicketRow; barWidth?: string }> = ({ ticket, barWidth = 'w-24' }) => {
  const sla = slaInfo(ticket);
  return (
    <div>
      <span
        className={cn(
          'whitespace-nowrap text-xs font-semibold',
          sla.state === 'breached' ? ALERT_TEXT : sla.state === 'at_risk' ? 'text-foreground' : 'text-muted-foreground'
        )}
      >
        {sla.text}
      </span>
      {sla.fraction !== null && (
        <div className={cn('mt-1 h-1 overflow-hidden rounded-full bg-muted', barWidth)}>
          <div
            className={cn(
              'h-full rounded-full',
              sla.state === 'breached' ? 'bg-red-600 dark:bg-red-400' : sla.state === 'at_risk' ? 'bg-foreground/70' : 'bg-muted-foreground/40'
            )}
            style={{ width: `${Math.round(sla.fraction * 100)}%` }}
          />
        </div>
      )}
    </div>
  );
};

const Avatar: React.FC<{ name?: string | null }> = ({ name }) =>
  name ? (
    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-bold text-foreground">
      {initials(name)}
    </span>
  ) : (
    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed border-muted-foreground/40 text-[10px] text-muted-foreground">
      –
    </span>
  );

const SafetyTag: React.FC<{ long?: boolean }> = ({ long }) => (
  <span className={cn('whitespace-nowrap rounded-md bg-red-50 px-1.5 py-0.5 text-[11px] font-semibold dark:bg-red-500/10', ALERT_TEXT)}>
    {long ? 'Safety escalation' : 'Safety'}
  </span>
);

const RegistrationTag: React.FC<{ info: NonNullable<ComplaintTicketRow['registration_incomplete']> }> = ({ info }) => (
  <span
    className="mt-0.5 block w-fit rounded-full border border-border px-1.5 text-[10px] font-medium text-muted-foreground"
    title={(info.missing_fields || []).length ? `Missing: ${(info.missing_fields || []).join(', ')}` : 'Registration incomplete'}
  >
    Registration incomplete
  </span>
);

const ReviewTag: React.FC = () => (
  <span
    className="mt-0.5 inline-block w-fit rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300"
    title="The Digital Companion wasn't sure about this ticket's issue(s) - please check them"
  >
    Needs review
  </span>
);

const KpiTile: React.FC<{ label: string; value: React.ReactNode; sub: string; alert?: boolean }> = ({ label, value, sub, alert }) => (
  <div className="rounded-xl border border-border bg-card px-4 py-3">
    <div className="text-xs font-semibold text-muted-foreground">{label}</div>
    <div className={cn('mt-0.5 text-2xl font-extrabold tabular-nums', alert && ALERT_TEXT)}>{value}</div>
    <div className="text-xs text-muted-foreground">{sub}</div>
  </div>
);

const EVENT_LABELS: Record<string, string> = {
  created: 'Ticket created',
  assigned: 'Assigned',
  status_changed: 'Status changed',
  priority_changed: 'Priority changed',
  comment: 'Note',
  contact_added_details: 'Contact added details',
  merged: 'Merged',
  linked: 'Linked',
};

const eventText = (event: NonNullable<ComplaintTicketRow['events']>[number], agentNames: Record<number, string>): string => {
  const p = event.payload || {};
  if (event.event_type === 'status_changed' && p.from && p.to) {
    return `Status: ${STATUS_LABELS[p.from] || p.from} → ${STATUS_LABELS[p.to] || p.to}`;
  }
  if (event.event_type === 'priority_changed' && p.to) return `Priority: ${p.from || 'normal'} → ${p.to}`;
  if (event.event_type === 'assigned' && p.assigned_agent_id) {
    return `Assigned to ${agentNames[p.assigned_agent_id] || `user #${p.assigned_agent_id}`}`;
  }
  return EVENT_LABELS[event.event_type] || event.event_type.replace(/_/g, ' ');
};

// ─── component ──────────────────────────────────────────────────────────

type Props = {
  highlightTicketNumber?: string | null;
  onOpenConversation?: (conversationId: number) => void;
};

const IssuedTicketsPanel: React.FC<Props> = ({ highlightTicketNumber, onOpenConversation }) => {
  // list
  const [tickets, setTickets] = useState<ComplaintTicketRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [tab, setTab] = useState('active');
  const [priority, setPriority] = useState('all');
  const [sortMode, setSortMode] = useState<SortMode>('attention');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [registrationIncompleteOnly, setRegistrationIncompleteOnly] = useState(false);
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [agents, setAgents] = useState<Array<{ id: number; name: string; notify_eligible: boolean }>>([]);

  // drawer
  const [detailsId, setDetailsId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ComplaintTicketRow | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState<'resolved' | 'closed' | null>(null);
  const [closeNote, setCloseNote] = useState('');
  const [commentDraft, setCommentDraft] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [suggestedReply, setSuggestedReply] = useState<string | null>(null);
  const [suggestError, setSuggestError] = useState<string | null>(null);

  const requestSeq = useRef(0);

  // Debounce search so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await complaintTicketsAPI.summary());
    } catch {
      setSummary(null); // tiles hide; the list still works
    }
  }, []);

  const loadList = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setListError(null);
    const statuses = STATUS_TABS.find((t) => t.key === tab)?.statuses;
    try {
      const res = await complaintTicketsAPI.list({
        status: statuses ? statuses.join(',') : undefined,
        priority: priority !== 'all' ? priority : undefined,
        q: search || undefined,
        order: sortMode,
        registration_incomplete: registrationIncompleteOnly ? true : undefined,
        needs_review: needsReviewOnly ? true : undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      if (seq !== requestSeq.current) return; // a newer request superseded this one
      setTickets(res.tickets || []);
      setTotal(res.total || 0);
    } catch (err: any) {
      if (seq !== requestSeq.current) return;
      setListError(errorMessage(err, 'Failed to load tickets'));
      setTickets([]);
      setTotal(0);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [tab, priority, search, sortMode, registrationIncompleteOnly, needsReviewOnly, page]);

  const refresh = useCallback(() => {
    loadList();
    loadSummary();
  }, [loadList, loadSummary]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadSummary();
    complaintTicketsAPI
      .assignableAgents()
      .then((res) => setAgents(res.agents || []))
      .catch(() => setAgents([]));
  }, [loadSummary]);

  const fetchDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    try {
      setDetail(await complaintTicketsAPI.get(id));
    } catch (err: any) {
      toast({ title: 'Could not load ticket', description: errorMessage(err, 'Please try again.'), variant: 'destructive' });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openDetails = useCallback(
    (ticket: ComplaintTicketRow) => {
      setDetailsId(ticket.id);
      setDetail(ticket); // show the row immediately, then load answers + activity
      setClosing(null);
      setCloseNote('');
      setCommentDraft('');
      setSuggestedReply(null);
      setSuggestError(null);
      fetchDetail(ticket.id);
    },
    [fetchDetail]
  );

  // Deep link (?ticket=TKT-…): look the ticket up directly — it may not be on the first page.
  useEffect(() => {
    if (!highlightTicketNumber) return;
    complaintTicketsAPI
      .getByNumber(highlightTicketNumber)
      .then((t: ComplaintTicketRow) => openDetails(t))
      .catch(() => toast({ title: `Ticket ${highlightTicketNumber} not found`, variant: 'destructive' }));
  }, [highlightTicketNumber, openDetails]);

  const closeDetails = () => {
    setDetailsId(null);
    setDetail(null);
  };

  // Runs an action, then refreshes the drawer, the list and the counts.
  const runAction = async (label: string, action: () => Promise<any>, success?: string) => {
    if (!detail) return;
    const id = detail.id;
    setBusy(true);
    try {
      const result = await action();
      if (result && Array.isArray(result.events)) setDetail(result);
      else await fetchDetail(id);
      if (success) toast({ title: success });
      refresh();
      return true;
    } catch (err: any) {
      toast({ title: label, description: errorMessage(err, 'Please try again.'), variant: 'destructive' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleStatus = (status: string) =>
    detail && runAction('Could not change status', () => complaintTicketsAPI.updateStatus(detail.id, { status }), `Status → ${STATUS_LABELS[status] || status}`);

  const handlePriority = (value: string) =>
    detail && runAction('Could not change priority', () => complaintTicketsAPI.updatePriority(detail.id, { priority: value }), `Priority → ${value}`);

  const handleAssign = (agentId?: number) =>
    detail && runAction('Could not assign ticket', () => complaintTicketsAPI.assign(detail.id, agentId ? { agent_id: agentId, notify: true } : { notify: true }), agentId ? 'Ticket reassigned' : 'Assigned to you');

  const handleClose = async () => {
    if (!detail || !closing) return;
    const ok = await runAction(
      closing === 'resolved' ? 'Could not resolve ticket' : 'Could not close ticket',
      () => complaintTicketsAPI.close(detail.id, { status: closing, resolution_note: closeNote.trim() || undefined }),
      `${detail.ticket_number} ${closing === 'resolved' ? 'resolved' : 'closed'}`
    );
    if (ok) {
      setClosing(null);
      setCloseNote('');
    }
  };

  const handleReopen = () =>
    detail && runAction('Could not reopen ticket', () => complaintTicketsAPI.reopen(detail.id), `${detail.ticket_number} reopened`);

  // 2026-10-04 (plan 5.9): "Edit issues"
  const [editingIssues, setEditingIssues] = useState(false);
  const [allCategories, setAllCategories] = useState<{ id: string; label: string }[]>([]);
  const [issueSelection, setIssueSelection] = useState<string[]>([]);
  const [issueReason, setIssueReason] = useState<(typeof CORRECTION_REASONS)[number]['value']>('wrong_category');
  const [issueNote, setIssueNote] = useState('');
  const [issueFilter, setIssueFilter] = useState('');

  const openIssueEditor = async () => {
    if (!detail) return;
    const current = (detail.issue_decisions || []).map((d) => d.category_id);
    setIssueSelection(current.length ? current : detail.category_id ? [detail.category_id] : []);
    setIssueReason('wrong_category');
    setIssueNote('');
    setIssueFilter('');
    setEditingIssues(true);
    if (!allCategories.length) {
      try {
        setAllCategories(await complaintTicketsAPI.categories());
      } catch (err: any) {
        toast({ title: 'Could not load categories', description: errorMessage(err, 'Please try again.'), variant: 'destructive' });
      }
    }
  };

  const saveIssues = async () => {
    if (!detail) return;
    const ok = await runAction(
      'Could not update the issues',
      () => complaintTicketsAPI.updateCategories(detail.id, {
        category_ids: issueSelection,
        reason: issueReason,
        note: issueNote.trim() || undefined,
      }),
      'Issues updated'
    );
    if (ok) setEditingIssues(false);
  };

  const handleConfirmReview = () =>
    detail && runAction('Could not mark as reviewed', () => complaintTicketsAPI.review(detail.id), 'Issues confirmed');

  const handleComment = async () => {
    if (!detail || !commentDraft.trim()) return;
    const ok = await runAction('Could not post note', () => complaintTicketsAPI.addComment(detail.id, { text: commentDraft.trim() }), 'Note posted');
    if (ok) setCommentDraft('');
  };

  const handleSuggestReply = async () => {
    if (!detail) return;
    setSuggesting(true);
    setSuggestError(null);
    try {
      const result = await complaintTicketsAPI.suggestReply(detail.id);
      setSuggestedReply(result.suggested_reply);
    } catch (err: any) {
      setSuggestError(errorMessage(err, 'Could not draft a reply'));
    } finally {
      setSuggesting(false);
    }
  };

  const agentNames = Object.fromEntries(agents.map((a) => [a.id, a.name])) as Record<number, string>;
  const tabCount = (statuses: string[] | null): number | null => {
    if (!summary) return null;
    if (!statuses) return summary.total;
    return statuses.reduce((sum, s) => sum + (summary.by_status[s] || 0), 0);
  };
  const firstRow = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastRow = Math.min(total, (page + 1) * PAGE_SIZE);
  const perms = detail?.permissions;
  const isDone = detail ? DONE_STATUSES.includes(detail.status) : false;
  const answers =
    detail?.answers && detail.answers.length > 0
      ? detail.answers
      : Object.entries(detail?.collected_answers || {}).map(([key, answer]) => ({ question_id: key, question: key.replace(/_/g, ' '), answer }));

  return (
    <div className="h-full min-h-0 overflow-auto">
      <div className="mx-auto max-w-[1320px] space-y-4 p-4 md:p-5">
        {/* KPI tiles */}
        {summary && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiTile label="Open" value={summary.open} sub={`${summary.open_unassigned} unassigned`} />
            <KpiTile label="Assigned to me" value={summary.open_assigned_to_me} sub="open, yours" />
            <KpiTile label="SLA breached" value={summary.sla_breached} sub="act on these first" alert={summary.sla_breached > 0} />
            <KpiTile
              label="Done"
              value={(summary.by_status.resolved || 0) + (summary.by_status.closed || 0)}
              sub="resolved + closed"
            />
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
            <div className="relative min-w-[220px] max-w-sm flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search ticket, name, phone, issue…"
                className="h-9 pl-9"
                aria-label="Search tickets"
              />
            </div>
            <div className="inline-flex flex-wrap gap-0.5 rounded-lg bg-muted p-[3px]" role="group" aria-label="Status">
              {STATUS_TABS.map((t) => {
                const n = tabCount(t.statuses);
                return (
                  <button
                    key={t.key}
                    type="button"
                    aria-pressed={tab === t.key}
                    onClick={() => {
                      setTab(t.key);
                      setPage(0);
                    }}
                    className={cn(
                      'rounded-md px-2.5 py-1 text-xs font-semibold transition-colors',
                      tab === t.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {t.label}
                    {n !== null && <span className="ml-1 tabular-nums opacity-70">{n}</span>}
                  </button>
                );
              })}
            </div>
            <Select
              value={priority}
              onValueChange={(v) => {
                setPriority(v);
                setPage(0);
              }}
            >
              <SelectTrigger className="h-9 w-[140px] text-sm" aria-label="Priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-sm">All priorities</SelectItem>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={p.value} className="text-sm">
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={sortMode}
              onValueChange={(v) => {
                setSortMode(v as SortMode);
                setPage(0);
              }}
            >
              <SelectTrigger className="h-9 w-[190px] text-sm" aria-label="Sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="text-sm">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              aria-pressed={registrationIncompleteOnly}
              onClick={() => {
                setRegistrationIncompleteOnly((v) => !v);
                setPage(0);
              }}
              title="Intake continued after the contact declined or didn't finish a required form"
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                registrationIncompleteOnly ? 'border-foreground/30 bg-muted text-foreground' : 'border-border text-muted-foreground hover:bg-muted'
              )}
            >
              Registration incomplete
            </button>
            <button
              type="button"
              aria-pressed={needsReviewOnly}
              onClick={() => {
                setNeedsReviewOnly((v) => !v);
                setPage(0);
              }}
              title="The Digital Companion wasn't sure about these tickets' issues"
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                needsReviewOnly ? 'border-foreground/30 bg-muted text-foreground' : 'border-border text-muted-foreground hover:bg-muted'
              )}
            >
              Needs review
            </button>
            <Button variant="ghost" size="icon" className="ml-auto h-9 w-9" onClick={refresh} disabled={loading} aria-label="Refresh">
              <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
            </Button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {loading && tickets.length === 0 ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : listError ? (
              <div className="flex flex-col items-center gap-3 px-4 py-12 text-center text-sm">
                <p className={ALERT_TEXT}>{listError}</p>
                <Button variant="outline" size="sm" onClick={refresh}>
                  Try again
                </Button>
              </div>
            ) : tickets.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-4 py-16 text-center text-sm text-muted-foreground">
                <Ticket className="h-6 w-6 opacity-50" />
                {search || priority !== 'all' || tab !== 'all' || registrationIncompleteOnly || needsReviewOnly ? 'No tickets match these filters' : 'No tickets yet'}
              </div>
            ) : (
              <table className={cn('w-full min-w-[900px] border-collapse text-sm', loading && 'opacity-60')}>
                <thead>
                  <tr className="border-b border-border text-left text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2.5">Ticket</th>
                    <th className="px-3 py-2.5">Issue</th>
                    <th className="px-3 py-2.5">Contact</th>
                    <th className="px-3 py-2.5">Assignee</th>
                    <th className="px-3 py-2.5">Status</th>
                    <th className="px-3 py-2.5">SLA</th>
                    <th className="px-3 py-2.5">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t) => (
                    <tr
                      key={t.id}
                      tabIndex={0}
                      onClick={() => openDetails(t)}
                      onKeyDown={(e) => e.key === 'Enter' && openDetails(t)}
                      className={cn(
                        'cursor-pointer border-b border-border last:border-b-0 hover:bg-muted/50',
                        detailsId === t.id && 'bg-muted/70'
                      )}
                    >
                      <td className="px-3 py-2.5 align-middle">
                        <div className="font-bold tabular-nums">{t.ticket_number}</div>
                        <PriorityLabel priority={t.priority} />
                      </td>
                      <td className="max-w-[300px] px-3 py-2.5 align-middle">
                        <div className="truncate font-semibold" title={categoryText(t)}>{categoryText(t)}</div>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          {t.is_safety_escalation && <SafetyTag />}
                          {t.needs_review && <ReviewTag />}
                          {t.persona_name && <span className="truncate">via {t.persona_name}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 align-middle">
                        <div className="font-semibold">{contactName(t)}</div>
                        {t.contact?.phone_number && <div className="text-xs tabular-nums text-muted-foreground">{t.contact.phone_number}</div>}
                        {t.registration_incomplete && <RegistrationTag info={t.registration_incomplete} />}
                      </td>
                      <td className="px-3 py-2.5 align-middle">
                        <div className="flex items-center gap-2">
                          <Avatar name={t.assigned_agent?.name} />
                          <span className={cn('truncate', !t.assigned_agent && 'text-muted-foreground')}>
                            {t.assigned_agent?.name || 'Unassigned'}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 align-middle">
                        <StatusPill status={t.status} />
                      </td>
                      <td className="px-3 py-2.5 align-middle">
                        <SlaCell ticket={t} />
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 align-middle text-muted-foreground">{timeAgo(t.updated_at || t.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Paging */}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
            <span className="tabular-nums">
              {total > 0 ? `Showing ${firstRow}–${lastRow} of ${total}` : ''}
            </span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="h-8 px-2" disabled={page === 0 || loading} onClick={() => setPage((p) => p - 1)}>
                <ChevronLeft className="h-4 w-4" /> Prev
              </Button>
              <span className="tabular-nums">
                Page {total === 0 ? 0 : page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
              </span>
              <Button variant="ghost" size="sm" className="h-8 px-2" disabled={lastRow >= total || loading} onClick={() => setPage((p) => p + 1)}>
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Details drawer */}
      <Sheet open={detailsId !== null} onOpenChange={(open) => !open && closeDetails()}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
          {detail && (
            <>
              <div className="border-b border-border px-5 pb-3.5 pr-12 pt-5">
                <div className="flex flex-wrap items-center gap-2">
                  <SheetTitle className="text-base font-extrabold tabular-nums">{detail.ticket_number}</SheetTitle>
                  <StatusPill status={detail.status} />
                  <PriorityLabel priority={detail.priority} />
                  {detail.is_safety_escalation && <SafetyTag long />}
                </div>
                <h2 className="mt-2 text-[17px] font-bold leading-snug">{categoryText(detail)}</h2>
                <SheetDescription className="text-xs">
                  Updated {timeAgo(detail.updated_at || detail.created_at)}
                  {detail.persona_name ? ` · ${detail.persona_name}` : ''}
                  {detail.resolution_mode === 'virtual_close' ? ' · Virtual close' : ''}
                </SheetDescription>
              </div>

              {/* Actions — only what this user may do (ticket.permissions) */}
              <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 px-5 py-3">
                {perms?.can_update_status && (
                  <Select value={detail.status} onValueChange={handleStatus} disabled={busy}>
                    <SelectTrigger className="h-8 w-[170px] text-xs" aria-label="Status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value} className="text-sm">
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {perms?.can_close && (
                  <>
                    <Button
                      size="sm"
                      className="h-8 bg-primary px-3 text-xs text-primary-foreground hover:bg-primary/90"
                      disabled={busy}
                      onClick={() => setClosing('resolved')}
                    >
                      ✓ Resolve
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 px-3 text-xs" disabled={busy} onClick={() => setClosing('closed')}>
                      Close
                    </Button>
                  </>
                )}
                {perms?.can_reopen && (
                  <Button size="sm" variant="outline" className="h-8 px-3 text-xs" disabled={busy} onClick={handleReopen}>
                    Reopen
                  </Button>
                )}
                {perms?.can_assign_self && !isDone && (
                  <Button size="sm" variant="outline" className="h-8 px-3 text-xs" disabled={busy} onClick={() => handleAssign()}>
                    Assign to me
                  </Button>
                )}
                {perms?.can_assign_others && agents.length > 0 && (
                  <Select
                    value={detail.assigned_agent_id ? String(detail.assigned_agent_id) : 'unassigned'}
                    onValueChange={(v) => v !== 'unassigned' && handleAssign(Number(v))}
                    disabled={busy}
                  >
                    <SelectTrigger className="h-8 w-[170px] text-xs" aria-label="Assignee">
                      <SelectValue placeholder="Assign to…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned" disabled className="text-sm">
                        Unassigned
                      </SelectItem>
                      {agents.map((a) => (
                        <SelectItem key={a.id} value={String(a.id)} className="text-sm">
                          {a.name}
                          {a.notify_eligible ? '' : ' · no WhatsApp phone'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {perms?.can_change_priority && (
                  <Select value={detail.priority || 'normal'} onValueChange={handlePriority} disabled={busy}>
                    <SelectTrigger className="h-8 w-[120px] text-xs" aria-label="Priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => (
                        <SelectItem key={p.value} value={p.value} className="text-sm">
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                <span className="ml-auto text-[11px] text-muted-foreground">
                  {perms?.can_update_status ? 'You can edit this ticket' : 'View only — not assigned to you'}
                </span>

                {closing && (
                  <div className="mt-1 w-full space-y-2 rounded-lg border border-border bg-background p-3">
                    <p className="text-xs font-semibold">
                      {closing === 'resolved' ? 'Resolve ticket' : 'Close ticket'} — add a note for the record (optional)
                    </p>
                    <Textarea
                      rows={2}
                      value={closeNote}
                      onChange={(e) => setCloseNote(e.target.value)}
                      placeholder={closing === 'resolved' ? 'What was done to fix it?' : 'Why is it being closed?'}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" className="h-8 bg-primary text-xs text-primary-foreground hover:bg-primary/90" disabled={busy} onClick={handleClose}>
                        {closing === 'resolved' ? 'Resolve' : 'Close ticket'}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 text-xs" disabled={busy} onClick={() => setClosing(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Contact</dt>
                    <dd className="mt-0.5 font-semibold">
                      {contactName(detail)}
                      {detail.contact?.phone_number && (
                        <div className="text-xs font-normal tabular-nums text-muted-foreground">{detail.contact.phone_number}</div>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Assignee</dt>
                    <dd className="mt-0.5 flex items-center gap-2 font-semibold">
                      <Avatar name={detail.assigned_agent?.name} />
                      <span className={cn(!detail.assigned_agent && 'font-normal text-muted-foreground')}>
                        {detail.assigned_agent?.name || 'Unassigned'}
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">SLA</dt>
                    <dd className="mt-0.5">
                      <SlaCell ticket={detail} barWidth="w-32" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Raised</dt>
                    <dd className="mt-0.5 font-semibold tabular-nums">{formatDateTime(detail.created_at)}</dd>
                  </div>
                </dl>

                {(detail.issue_decisions || []).length > 0 && (
                  <section>
                    <div className="mb-2 flex items-center gap-2">
                      <h3 className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Issues</h3>
                      {detail.needs_review && <ReviewTag />}
                    </div>
                    <ul className="space-y-1.5">
                      {(detail.issue_decisions || []).map((d, index) => {
                        const label = d.label || d.category_id;
                        return (
                          <li
                            key={`${d.category_id}-${index}`}
                            className={cn(
                              'rounded-lg border px-3 py-2 text-xs',
                              d.needs_review ? 'border-amber-500/40 bg-amber-500/5' : 'border-border'
                            )}
                          >
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                              <span className="font-semibold text-foreground">{label}</span>
                              {d.main && <span className="text-[10px] font-semibold uppercase text-muted-foreground">main</span>}
                              <span className="text-muted-foreground">
                                {DECISION_LABELS[d.decision] || d.decision}
                                {typeof d.confidence === 'number' ? ` · ${Math.round(d.confidence * 100)}%` : ''}
                              </span>
                            </div>
                            {d.question && (
                              <div className="mt-1 text-muted-foreground">
                                Asked: “{d.question}”{d.answer ? <> → “{d.answer}”</> : null}
                              </div>
                            )}
                            {d.needs_review && d.runner_up && (
                              <div className="mt-0.5 text-muted-foreground">Could also be: {d.runner_up}</div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    {detail.permissions?.can_update_status && !editingIssues && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {detail.needs_review && (
                          <Button size="sm" variant="outline" className="h-8 text-xs" disabled={busy} onClick={handleConfirmReview}>
                            Issues are correct - mark reviewed
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-8 text-xs" disabled={busy} onClick={openIssueEditor}>
                          Edit issues
                        </Button>
                      </div>
                    )}
                    {editingIssues && (
                      <div className="mt-2 space-y-2 rounded-lg border border-border p-3">
                        <div className="text-xs font-semibold">Correct issues</div>
                        <Input
                          value={issueFilter}
                          onChange={(e) => setIssueFilter(e.target.value)}
                          placeholder="Search categories"
                          className="h-8 text-xs"
                        />
                        <div className="max-h-48 space-y-1 overflow-y-auto">
                          {allCategories
                            .filter((c) =>
                              issueSelection.includes(c.id) ||
                              !issueFilter.trim() ||
                              `${c.label} ${c.id}`.toLowerCase().includes(issueFilter.trim().toLowerCase())
                            )
                            .map((c) => (
                              <label key={c.id} className="flex cursor-pointer items-start gap-2 text-xs">
                                <input
                                  type="checkbox"
                                  className="mt-0.5"
                                  checked={issueSelection.includes(c.id)}
                                  onChange={(e) =>
                                    setIssueSelection((prev) =>
                                      e.target.checked ? [...prev, c.id] : prev.filter((x) => x !== c.id)
                                    )
                                  }
                                />
                                <span>
                                  {c.label} <span className="text-muted-foreground">({c.id})</span>
                                </span>
                              </label>
                            ))}
                        </div>
                        <div className="space-y-1">
                          <div className="text-xs font-semibold">What was wrong?</div>
                          <div className="flex flex-wrap gap-1.5">
                            {CORRECTION_REASONS.map((r) => (
                              <button
                                key={r.value}
                                type="button"
                                onClick={() => setIssueReason(r.value)}
                                className={cn(
                                  'rounded-full border px-2.5 py-1 text-[11px] font-semibold',
                                  issueReason === r.value ? 'border-foreground/30 bg-muted text-foreground' : 'border-border text-muted-foreground hover:bg-muted'
                                )}
                              >
                                {r.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <Input
                          value={issueNote}
                          onChange={(e) => setIssueNote(e.target.value)}
                          placeholder="Note (optional)"
                          className="h-8 text-xs"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="h-8 text-xs"
                            disabled={busy || (issueReason !== 'not_a_ticket' && issueSelection.length === 0)}
                            onClick={saveIssues}
                          >
                            Save
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setEditingIssues(false)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </section>
                )}

                {detail.registration_incomplete && (
                  <div className="rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">Registration incomplete.</span>{' '}
                    {(detail.registration_incomplete.missing_fields || []).length
                      ? `Missing: ${(detail.registration_incomplete.missing_fields || []).join(', ')}`
                      : 'The contact declined or did not finish a required form.'}
                  </div>
                )}

                <section>
                  <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Questions &amp; answers</h3>
                  {answers.length > 0 ? (
                    <ol className="space-y-2">
                      {answers.map((row, index) => (
                        <li key={row.question_id} className="rounded-lg border border-border px-3 py-2.5">
                          <div className="flex gap-2 text-xs font-semibold text-muted-foreground">
                            <span className="inline-flex h-5 min-w-[22px] items-center justify-center rounded-md bg-muted px-1 text-[11px] text-foreground">
                              Q{index + 1}
                            </span>
                            <span>{row.question}</span>
                          </div>
                          <p className="mt-1.5 whitespace-pre-wrap text-sm">{row.answer}</p>
                        </li>
                      ))}
                    </ol>
                  ) : detail.summary ? (
                    <p className="whitespace-pre-wrap text-sm">{detail.summary}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">No answers were collected.</p>
                  )}
                </section>

                <section className="space-y-2 rounded-lg border border-dashed border-border p-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xs font-semibold">Suggested reply</h3>
                    <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 text-xs" disabled={suggesting} onClick={handleSuggestReply}>
                      {suggesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                      {suggestedReply ? 'Regenerate' : 'Draft with AI'}
                    </Button>
                  </div>
                  {suggestError && <p className={cn('text-xs', ALERT_TEXT)}>{suggestError}</p>}
                  {suggestedReply ? (
                    <div className="space-y-1.5">
                      <p className="whitespace-pre-wrap rounded-md bg-muted/60 p-2 text-sm">{suggestedReply}</p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1.5 text-xs"
                        onClick={() => navigator.clipboard?.writeText(suggestedReply).then(() => toast({ title: 'Copied' })).catch(() => {})}
                      >
                        <Copy className="h-3 w-3" /> Copy
                      </Button>
                      <p className="text-[11px] text-muted-foreground">AI-drafted — review before sending. Paste it into the conversation to send it.</p>
                    </div>
                  ) : (
                    !suggestError &&
                    !suggesting && <p className="text-xs text-muted-foreground">Drafts a reply from this ticket's answers for you to review and send.</p>
                  )}
                </section>

                <section>
                  <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Activity</h3>
                  {detail.events && detail.events.length > 0 ? (
                    <ol className="space-y-3 border-l-2 border-border pl-4">
                      {[...detail.events].reverse().map((event) => (
                        <li key={event.id} className="relative text-sm">
                          <span className="absolute -left-[23px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-muted-foreground bg-background" />
                          <div>
                            <span className="font-semibold">{eventText(event, agentNames)}</span>
                            {event.created_at && <span className="text-xs text-muted-foreground"> · {formatDateTime(event.created_at)}</span>}
                          </div>
                          {(event.payload?.text || event.payload?.note) && (
                            <p className="mt-1 whitespace-pre-wrap rounded-md bg-muted px-2.5 py-1.5 text-sm">{event.payload?.text || event.payload?.note}</p>
                          )}
                        </li>
                      ))}
                    </ol>
                  ) : detailLoading ? (
                    <p className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">No activity yet.</p>
                  )}

                  {perms?.can_comment !== false && (
                    <div className="mt-3 space-y-2">
                      <Textarea
                        rows={2}
                        value={commentDraft}
                        onChange={(e) => setCommentDraft(e.target.value)}
                        placeholder="Internal note — visible to staff only, never sent to the contact"
                      />
                      <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!commentDraft.trim() || busy} onClick={handleComment}>
                        Post note
                      </Button>
                    </div>
                  )}
                </section>
              </div>

              {detail.conversation_id && onOpenConversation && (
                <div className="border-t border-border px-5 py-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 gap-2"
                    onClick={() => {
                      const conversationId = detail.conversation_id!;
                      closeDetails();
                      onOpenConversation(conversationId);
                    }}
                  >
                    <MessageSquare className="h-4 w-4" /> View chat
                  </Button>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default IssuedTicketsPanel;
